import React from 'react';
import SEOHead from '../components/SEOHead';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Heart, Zap, Globe, Shield, Coffee, TrendingUp,
  ArrowRight, CheckCircle2, MapPin, Clock, Briefcase
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

// PLACEHOLDER: replace all job listings with real open roles
const openRoles = [
  {
    title: 'Senior Backend Engineer',
    team: 'Engineering',
    location: 'Remote',
    type: 'Full-time',
    description: 'Build and scale the FastAPI services and data pipelines that power our AI voice agents and analytics platform.',
  },
  {
    title: 'ML / AI Engineer',
    team: 'AI Research',
    location: 'Remote',
    type: 'Full-time',
    description: 'Improve speech recognition, NLU, and conversational AI models embedded in our voice agent products.',
  },
  {
    title: 'Product Manager, AI Digital Assistance',
    team: 'Product',
    location: 'Remote',
    type: 'Full-time',
    description: 'Own the roadmap for our AI Voice IT Support and AI Customer Care products from discovery through launch.',
  },
  {
    title: 'Enterprise Account Executive',
    team: 'Sales',
    location: 'Remote (USA)',
    type: 'Full-time',
    description: 'Drive new business within F500 and mid-market enterprise accounts across financial services, healthcare, and technology verticals.',
  },
  {
    title: 'Customer Success Manager',
    team: 'Customer Success',
    location: 'Remote (USA)',
    type: 'Full-time',
    description: 'Own the post-sale relationship with key enterprise customers, driving adoption, expansion, and renewal.',
  },
  {
    title: 'UX / Product Designer',
    team: 'Design',
    location: 'Remote',
    type: 'Full-time',
    description: 'Design intuitive, accessible interfaces for complex enterprise data workflows and AI-powered interaction models.',
  },
];

// PLACEHOLDER: replace with real perks and benefits
const perks = [
  { icon: Globe, title: 'Remote-First', desc: 'Work from anywhere with flexible hours. We have team members across 12 countries.' },
  { icon: TrendingUp, title: 'Equity for Everyone', desc: 'Every full-time employee receives meaningful equity. We all share in what we build.' },
  { icon: Shield, title: 'Comprehensive Health', desc: 'Full medical, dental, and vision coverage. Mental health support included.' },
  { icon: Coffee, title: 'Learning Budget', desc: '£2,000 annual learning & development budget. Conferences, courses, books: your call.' },
  { icon: Zap, title: 'Home Office Stipend', desc: '£1,000 setup allowance plus a monthly coworking allowance for when you need a change of scene.' },
  { icon: Heart, title: 'Unlimited PTO', desc: 'Take the time you need. We trust our team to manage their own time off responsibly.' },
];

// PLACEHOLDER: replace with real values / culture content
const values = [
  {
    heading: 'Build things that matter',
    body: 'We focus on enterprise problems that are genuinely hard and have real business impact, not incremental features chasing a growth metric.',
  },
  {
    heading: 'Move fast without breaking trust',
    body: 'Speed matters, but so does reliability. We ship confidently, instrument everything, and take security and data privacy seriously by default.',
  },
  {
    heading: 'Own the outcome, not the task',
    body: 'We hire people who identify the right problem, not just people who execute a brief. Everyone has context to make good decisions.',
  },
  {
    heading: 'Diverse perspectives, shared mission',
    body: 'Our best ideas come from the collision of different backgrounds. We actively build a team that reflects the enterprises we serve.',
  },
];

const CareersPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead page="careers" />

      {/* Hero */}
      <section className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-blue-500/10 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl space-y-6">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600">Careers at Digian.ai</p>
            <h1 className="text-5xl font-bold text-slate-900">
              Help us make enterprise data speak for itself
            </h1>
            {/* PLACEHOLDER: update with real employer value proposition */}
            <p className="text-lg text-slate-600 leading-relaxed">
              We're a fast-growing, remote-first team building the AI-powered tools that help enterprises get more value from their data. If you're excited about voice AI, conversational analytics, and the future of enterprise software, we'd love to hear from you.
            </p>
            <div className="flex gap-4">
              <a href="#open-roles"><Button size="lg">See Open Roles <ArrowRight className="ml-2 w-4 h-4" /></Button></a>
              <Link to="/about"><Button size="lg" variant="outline">About the Team</Button></Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      {/* PLACEHOLDER: update with real headcount, countries, founding year */}
      <section className="bg-white border-y border-slate-100 py-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { value: '2023', label: 'Founded' },
              { value: 'Early Stage', label: 'Growing fast' },
              { value: 'Remote-first', label: 'Work from anywhere' },
              { value: 'Hiring Now', label: 'Join us early' },
            ].map((s, i) => (
              <motion.div key={s.label} {...fadeUp} transition={{ delay: i * 0.1 }}>
                <p className="text-4xl font-extrabold text-indigo-600">{s.value}</p>
                <p className="text-sm text-slate-500 mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values / culture */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">How we work</p>
            <h2 className="text-3xl font-bold text-slate-900">What it's like to be on the team</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {values.map((v, i) => (
              <motion.div key={v.heading} {...fadeUp} transition={{ delay: i * 0.1 }}
                className="bg-white rounded-xl border border-slate-200 p-8">
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="w-6 h-6 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">{v.heading}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{v.body}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Perks */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Benefits</p>
            <h2 className="text-3xl font-bold text-slate-900">What we offer</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {perks.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div key={p.title} {...fadeUp} transition={{ delay: i * 0.08 }}
                  className="bg-slate-50 rounded-xl border border-slate-200 p-6">
                  <div className="w-11 h-11 rounded-xl bg-indigo-100 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{p.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Open roles */}
      <section id="open-roles" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Open Roles</p>
            <h2 className="text-3xl font-bold text-slate-900">Current openings</h2>
          </div>
          <div className="space-y-4">
            {openRoles.map((role, i) => (
              <motion.div key={role.title} {...fadeUp} transition={{ delay: i * 0.07 }}
                className="bg-white rounded-xl border border-slate-200 p-6 hover:border-indigo-300 hover:shadow-sm transition-all">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h3 className="font-semibold text-slate-900">{role.title}</h3>
                      <span className="text-xs bg-indigo-100 text-indigo-700 font-medium px-2.5 py-0.5 rounded-full">{role.team}</span>
                    </div>
                    <p className="text-sm text-slate-600 mb-3 leading-relaxed">{role.description}</p>
                    <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{role.location}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{role.type}</span>
                      <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" />{role.team}</span>
                    </div>
                  </div>
                  <a
                    href={`mailto:careers@digian.ai?subject=Application for ${encodeURIComponent(role.title)}&body=Hi digian.ai team,%0D%0A%0D%0AI'd like to apply for the ${encodeURIComponent(role.title)} role (${encodeURIComponent(role.team)}).%0D%0A%0D%0APlease find my details below:%0D%0A%0D%0AName:%0D%0ALinkedIn / Portfolio:%0D%0A%0D%0AThank you!`}
                  >
                    <Button variant="outline" size="sm" className="whitespace-nowrap">Apply Now</Button>
                  </a>
                </div>
              </motion.div>
            ))}
          </div>

          {/* No match */}
          <motion.div {...fadeUp} className="mt-10 bg-indigo-50 rounded-2xl border border-indigo-100 p-8 text-center">
            <h3 className="font-semibold text-slate-900 mb-2">Don't see the right role?</h3>
            <p className="text-sm text-slate-600 mb-4">
              We're always interested in exceptional people. Send us a note and tell us what you'd bring to the team.
            </p>
            {/* PLACEHOLDER: link to general application form */}
            <Link to="/contact"><Button>Get in Touch</Button></Link>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-indigo-600 to-violet-600 py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div {...fadeUp} className="space-y-6">
            <h2 className="text-4xl font-bold text-white">Ready to do the best work of your career?</h2>
            <p className="text-xl text-indigo-100">
              Join a team that's building AI tools that enterprises actually rely on.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#open-roles"><Button size="lg" variant="secondary">View Open Roles</Button></a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-indigo-600">
                  Send a General Application
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
